import React, { useContext, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useQuery } from '@apollo/client';
import { checkIfLoggedIn } from '@blaze-cms/core-auth-ui';
import { useRouter } from 'next/router';
import { useGetSingleEntitySchema, generateSingleItemQuery } from '@blaze-cms/react-page-builder';
import { MainContext } from '@blaze-cms/nextjs-components';
import RegistrationForm from './RegistrationForm';
import { GET_COUNTRIES } from '../../services/querys';
import { FORM_SCHEMA } from './constants';
import buildSelectOptions from '../helpers/build-select-options';

const RegistrationFormFactory = ({
  redirectUrl,
  parent,
  subscriptionTypeId,
  subscriptionId: settingsSubscriptionId,
  privacyPolicyUrl,
  emailUpdatesInfo,
  ...props
}) => {
  const { isPreview } = useContext(MainContext);
  const { itemId, itemEntity } = parent;
  const { data: { getEntitySchema = {} } = {}, loading: schemaLoading } =
    useGetSingleEntitySchema(itemEntity);
  const { actions: { getPublished, get } = {} } = getEntitySchema;
  const query = useMemo(() => {
    const getAction = isPreview ? get : getPublished;
    return generateSingleItemQuery(getAction, 'id,subscriptionId');
  }, [isPreview, get, getPublished]);
  const { data: { entityData = {} } = {}, loading: itemQueryLoading } = useQuery(query, {
    variables: { id: itemId },
    skip: subscriptionTypeId || settingsSubscriptionId || schemaLoading
  });
  const router = useRouter();
  const { isLoggedIn } = checkIfLoggedIn();
  const { data: { countries = [] } = {}, loading: countriesLoading } = useQuery(GET_COUNTRIES, {
    skip: isLoggedIn
  });

  if (isLoggedIn) {
    router.push(redirectUrl || '/');
    return null;
  }
  if (countriesLoading || schemaLoading || itemQueryLoading) return null;
  const countryOptions = buildSelectOptions(countries);
  const initialSchema = FORM_SCHEMA({ privacyPolicyUrl, emailUpdatesInfo });
  initialSchema.properties.country.options = countryOptions;
  initialSchema.properties.country.enum =
    countryOptions.length > 0 ? countryOptions.map(([v, _]) => v) : undefined;

  let subscriptionId = settingsSubscriptionId;
  if (!subscriptionTypeId && !settingsSubscriptionId) {
    const { subscriptionId: recordSubscriptionId } = entityData;
    subscriptionId = recordSubscriptionId;
  }

  if (!subscriptionId && !subscriptionTypeId) {
    return 'Error: No subscription or subscription type set for registration';
  }

  return (
    <RegistrationForm
      {...props}
      subscriptionId={subscriptionId}
      subscriptionTypeId={subscriptionTypeId}
      redirectUrl={redirectUrl}
      initialSchema={initialSchema}
      countries={countries}
    />
  );
};

RegistrationFormFactory.propTypes = {
  redirectUrl: PropTypes.string,
  parent: PropTypes.object.isRequired,
  subscriptionTypeId: PropTypes.string.isRequired,
  subscriptionId: PropTypes.string.isRequired,
  privacyPolicyUrl: PropTypes.string,
  emailUpdatesInfo: PropTypes.string
};

RegistrationFormFactory.defaultProps = {
  redirectUrl: null,
  privacyPolicyUrl: '/privacy-policy',
  emailUpdatesInfo: ''
};

export default RegistrationFormFactory;
